'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  BodyStrong,
  EndpointMethodBadge,
  IconButton,
  MonoDefault,
  ArchivedLifecycleBadge,
  SeverityIndicator,
  SignalSourceTag,
  Tooltip,
  getStatusIcon,
  isArchivedLifecycle,
} from '@terra/ui-design-system';
import { ListPageEmptyCell } from '../../common/ListPageEmptyCell';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { OpenMainContentTabInput } from '../../../store/layoutStore';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { ArchivedActionGate } from '../../common/ArchivedActionGate';
import { SignalCategorySelector } from '../SignalCategorySelector';
import { SignalStatusSelector } from '../SignalStatusSelector';
import { rowClickBlockerProps } from '../rowClickBlocker';
import {
  resolveLatestSignalTrace,
  resolveSignalDomain,
  resolveSignalEndpoint,
  toFindingSeverity,
} from './signals-list.model';
import {
  buildSignalExecutionTab,
  buildSignalFindingTab,
} from './signal-tabs.model';
import { useSignalsTableContext } from './SignalsTableContext';

type SignalCellProps = { signal: GroupedSignal };

function SignalsCellLink({
  tab,
  children,
}: {
  tab: OpenMainContentTabInput | undefined;
  children: ReactNode;
}) {
  const { navigation } = useSignalsTableContext();

  return (
    <HStack
      gap={2}
      minW={0}
      maxW="full"
      cursor={tab ? 'pointer' : 'default'}
      _hover={tab ? { textDecoration: 'underline' } : undefined}
      {...rowClickBlockerProps}
      onClick={(event) => {
        event.stopPropagation();
        navigation.openTab(tab);
      }}
      onContextMenu={(event) => navigation.openTabContextMenu({ event, tab })}
    >
      {children}
    </HStack>
  );
}

function SignalsCellEditor({
  signal,
  children,
}: SignalCellProps & { children: ReactNode }) {
  const tLifecycle = useTranslations('designSystem.lifecycle');

  return (
    <Box display="inline-flex" minW={0} {...rowClickBlockerProps}>
      <ArchivedActionGate
        isArchived={isArchivedLifecycle(signal.lifecycle)}
        tooltip={tLifecycle('archivedSignalActionTooltip')}
      >
        {children}
      </ArchivedActionGate>
    </Box>
  );
}

export function SignalNameCell({ signal }: SignalCellProps) {
  const t = useTranslations('proxy.signals.table');
  const tDrawer = useTranslations('designSystem.signals.drawer');
  const { expandedSignalIds, onToggleExpanded } = useSignalsTableContext();
  const isExpanded = expandedSignalIds.has(signal.id);
  const toggleLabel = isExpanded ? t('hideDetails') : t('showDetails');
  const title = signal.description?.trim() || tDrawer('title');

  return (
    <HStack gap={1.5} minW={0} w="full">
      <Box flexShrink={0} display="inline-flex" {...rowClickBlockerProps}>
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={toggleLabel}
          aria-expanded={isExpanded}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpanded(signal.id);
          }}
        >
          {isExpanded ? (
            <ArrowDown01Icon size={14} />
          ) : (
            <ArrowRight01Icon size={14} />
          )}
        </IconButton>
      </Box>
      <ArchivedLifecycleBadge lifecycle={signal.lifecycle} />
      <Tooltip
        title={title}
        positioning={{ placement: 'bottom-start' }}
        whiteSpace="normal"
      >
        <Box minW={0} flex={1}>
          <BodyStrong color="fg.neutral.default" truncate>
            {title}
          </BodyStrong>
        </Box>
      </Tooltip>
    </HStack>
  );
}

export function SignalDomainCell({ signal }: SignalCellProps) {
  const domain = resolveSignalDomain(signal);

  if (!domain) {
    return <ListPageEmptyCell />;
  }

  return (
    <BodyDefault color="fg.neutral.muted" truncate title={domain}>
      {domain}
    </BodyDefault>
  );
}

export function SignalEndpointCell({ signal }: SignalCellProps) {
  const { navigation } = useSignalsTableContext();
  const endpoint = resolveSignalEndpoint(signal);

  if (!endpoint) {
    return <ListPageEmptyCell />;
  }

  return (
    <SignalsCellLink tab={navigation.getRelatedAssetTab(signal)}>
      <Box flexShrink={0} display="inline-flex">
        <EndpointMethodBadge method={endpoint.method} />
      </Box>
      <MonoDefault color="fg.neutral.muted" truncate title={endpoint.path}>
        {endpoint.path}
      </MonoDefault>
    </SignalsCellLink>
  );
}

export function SignalStatusCell({ signal }: SignalCellProps) {
  const { navigation, onUpdateSignal } = useSignalsTableContext();
  const finding = signal.linkedFinding;

  return (
    <SignalsCellEditor signal={signal}>
      <SignalStatusSelector
        signalId={signal.id}
        currentStage={signal.stage}
        currentStatus={signal.status}
        currentBlockedReason={signal.blockedReason}
        disabled={isArchivedLifecycle(signal.lifecycle)}
        relatedFinding={
          finding ? { id: finding.id, title: finding.title } : undefined
        }
        onOpenRelatedFinding={() =>
          navigation.openTab(buildSignalFindingTab(signal))
        }
        onStatusChange={({ stage, status }) =>
          onUpdateSignal(signal.id, { stage, status })
        }
      />
    </SignalsCellEditor>
  );
}

export function SignalStageCell({ signal }: SignalCellProps) {
  const tStage = useTranslations('designSystem.signals.lifecycle.stage');

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {tStage(signal.stage)}
    </BodyDefault>
  );
}

export function SignalSourceCell({ signal }: SignalCellProps) {
  if (!signal.source) {
    return <ListPageEmptyCell />;
  }

  return (
    <Badge variant="outline" size="sm" maxW="full" truncate>
      <SignalSourceTag source={signal.source} size={12} />
    </Badge>
  );
}

export function SignalCategoryCell({ signal }: SignalCellProps) {
  const { isRejectedView, onUpdateSignal } = useSignalsTableContext();

  return (
    <SignalsCellEditor signal={signal}>
      <SignalCategorySelector
        signalId={signal.id}
        currentCategory={signal.category}
        disabled={isRejectedView || isArchivedLifecycle(signal.lifecycle)}
        onCategoryChange={(category) => onUpdateSignal(signal.id, { category })}
      />
    </SignalsCellEditor>
  );
}

export function SignalExecutionCell({ signal }: SignalCellProps) {
  const tStatus = useTranslations('designSystem.executions.status');
  const trace = resolveLatestSignalTrace(signal);

  if (!trace) {
    return <ListPageEmptyCell />;
  }

  return (
    <SignalsCellLink tab={buildSignalExecutionTab({ signal, trace })}>
      <Box flexShrink={0} display="inline-flex">
        {getStatusIcon(trace.status, 12)}
      </Box>
      <BodyDefault color="fg.neutral.muted" truncate>
        {`${trace.name.charAt(0).toUpperCase()}${trace.name.slice(1)} · ${tStatus(trace.status)}`}
      </BodyDefault>
    </SignalsCellLink>
  );
}

export function SignalFindingCell({ signal }: SignalCellProps) {
  const finding = signal.linkedFinding;

  if (!finding) {
    return <ListPageEmptyCell />;
  }

  const severity = toFindingSeverity(finding.severity);

  return (
    <SignalsCellLink tab={buildSignalFindingTab(signal)}>
      {severity ? (
        <Box flexShrink={0} display="inline-flex">
          <SeverityIndicator severity={severity} size="md" />
        </Box>
      ) : null}
      <BodyDefault color="fg.neutral.muted" truncate title={finding.title}>
        {finding.title}
      </BodyDefault>
    </SignalsCellLink>
  );
}

export function SignalCreatedAtCell({ signal }: SignalCellProps) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(signal.createdAt))}
    </BodyDefault>
  );
}

export function SignalAppWideCell({ signal }: SignalCellProps) {
  const tCommon = useTranslations('common');
  const tDrawer = useTranslations('designSystem.signals.drawer');

  if (signal.isAppWide !== true) {
    return <ListPageEmptyCell />;
  }

  return (
    <Tooltip title={tDrawer('appWideSignalTooltip')}>
      <BodyDefault color="fg.neutral.muted">{tCommon('yes')}</BodyDefault>
    </Tooltip>
  );
}
